import { type FormEvent, type ReactElement, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "ky";

import { login } from "@/api/auth";
import { Btn2 } from "@/components/hifi/Btn2";
import { Pill } from "@/components/hifi/Pill";
import { useAuthStore } from "@/stores/auth";

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(email, password);
      setSession(res.user, res.token.access_token);
      await navigate({ to: "/" });
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 401) {
        setError("Invalid email or password.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-n-75 p-6 font-sans text-n-900">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-2 text-[16px] font-bold text-white shadow-2"
            style={{
              background: "linear-gradient(135deg, var(--a-500), var(--a-700))",
            }}
          >
            C
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-[-0.3px]">
              CSMP Risk Manager
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3px] text-n-500">
              Physical Security Risk Assessment
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="rounded-3 border border-n-150 bg-white p-6 shadow-2"
          noValidate
        >
          <div className="mb-1 flex items-center justify-between">
            <h1 className="text-[16px] font-semibold text-n-900">Sign in</h1>
            <Pill variant="accent">CSMP</Pill>
          </div>
          <p className="text-[12px] text-n-500">
            Use your workspace credentials.
          </p>

          <label className="mt-5 block text-[11px] font-medium text-n-600">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              required
              autoComplete="email"
              className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
          </label>

          <label className="mt-4 block text-[11px] font-medium text-n-600">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              required
              autoComplete="current-password"
              className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            />
          </label>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
            >
              {error}
            </div>
          )}

          <div className="mt-5">
            <Btn2
              variant="accent"
              size="lg"
              full
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Btn2>
          </div>

          <p className="mt-4 text-[11px] text-n-500">
            No account?{" "}
            <a
              href="/register"
              className="font-medium text-a-600 no-underline hover:underline"
            >
              Create one
            </a>
          </p>
        </form>
      </div>
    </main>
  );
}
